import { useCallback, useEffect, useRef, useState } from 'react'

/**
 * Copy a string, and say out loud whether it worked.
 *
 * Split out of `Code` to keep that component under the size cap, but it
 * earns its own module anyway: the announcement is the hard part. The
 * copy button is a two-character glyph, so a screen reader user has no
 * visual confirmation to fall back on, and a copy that silently did
 * nothing is indistinguishable from one that worked until they paste.
 */

/** How long the message stays on screen. Long enough for a screen reader
 * to reach the live region, short enough not to linger over the next
 * click. */
const MESSAGE_MS = 2000

/**
 * The clipboard, if this page actually has one.
 *
 * The DOM typings declare `navigator.clipboard` as always present, and it
 * is not: it is absent outside a secure context and absent in jsdom. A
 * plain `=== undefined` on it is therefore pruned as dead code by the
 * type checker (and flagged by `no-unnecessary-condition`) while being
 * the single branch this component most needs. Reading it through
 * `unknown` first is what makes the check real; the cast at the end is
 * guarded by the `writeText` probe above it, not by hope.
 */
function getClipboard(): Clipboard | undefined {
  const candidate: unknown = navigator.clipboard
  if (
    typeof candidate === 'object' &&
    candidate !== null &&
    'writeText' in candidate
  ) {
    return candidate as Clipboard
  }
  return undefined
}

export interface CopyToClipboard {
  /** `null` when there is nothing to announce. Render it into a live
   * region that is ALREADY mounted — a `role="status"` node created at
   * the same moment as its text is routinely missed. */
  message: string | null
  copy: () => void
}

export function useCopyToClipboard(text: string): CopyToClipboard {
  const [message, setMessage] = useState<string | null>(null)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    // Clearing on unmount matters here because a copy button usually sits
    // in a table row that the next poll replaces.
    return () => {
      if (timerRef.current !== null) clearTimeout(timerRef.current)
    }
  }, [])

  const announce = useCallback((announcement: string) => {
    setMessage(announcement)
    if (timerRef.current !== null) clearTimeout(timerRef.current)
    timerRef.current = setTimeout(() => {
      setMessage(null)
    }, MESSAGE_MS)
  }, [])

  const copy = useCallback(() => {
    const clipboard = getClipboard()
    if (clipboard === undefined) {
      announce('Copy unavailable')
      return
    }
    // `void`: a click handler cannot be async, and both outcomes are
    // handled here, so there is nothing left for a caller to await.
    void clipboard.writeText(text).then(
      () => {
        announce('Copied')
      },
      (error: unknown) => {
        announce(
          error instanceof Error
            ? `Copy failed: ${error.message}`
            : 'Copy failed',
        )
      },
    )
  }, [announce, text])

  return { message, copy }
}
